import React, { useState, useEffect, useRef, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { ShieldCheck, LoaderCircle, Check, RotateCcw, ArrowDown } from 'lucide-react';
import { useOrg } from '../../hooks/useOrg';
import { api } from '../../lib/api';
import { renderLegalMarkdown } from './renderLegalMarkdown';

// Blocking execution of the Business Associate Agreement.
//
// 31 organizations reached production without one: 17 signed up before any BAA
// notice existed, 12 saw a link that returned 404, and 2 law firms were never
// shown it at all while being recorded as having accepted it. A dismissible
// banner would have left a long tail, and the tail is exactly the evidence gap.
//
// Only the founder (is_primary_org) can bind the organization. Everyone else
// passes straight through — blocking a receptionist from working over a
// contract they cannot sign would be pointless.
export default function BaaSigningGate({ children }) {
  const { currentOrg } = useOrg();
  const [state, setState] = useState({ loading: true, blocked: false });
  const [doc, setDoc] = useState(null);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [readAll, setReadAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const sigRef = useRef(null);
  const scrollRef = useRef(null);

  const orgId = currentOrg?.id;

  useEffect(() => {
    if (!orgId) { setState({ loading: false, blocked: false }); return undefined; }
    let alive = true;
    (async () => {
      try {
        const s = await api.baaStatus(orgId);
        if (!alive) return;
        // Not signed AND this user can sign it -> block. Not signed and they
        // cannot sign -> let them work; the founder gets prompted.
        const blocked = s.blocking != null ? s.blocking : (!s.signed && s.can_sign);
        // A new version after a signed earlier one: a banner until the
        // effective date, then the gate.
        const pending = !s.signed && s.can_sign && !blocked;
        setState({ loading: false, blocked, pending, effective: s.effective_for_existing || null, version: s.current_version });
        if (!s.signed && s.can_sign) {
          const d = await api.baaDocument(orgId);
          if (alive) setDoc(d);
        }
      } catch {
        // Never lock someone out of the product because a status check failed.
        if (alive) setState({ loading: false, blocked: false });
      }
    })();
    return () => { alive = false; };
  }, [orgId]);

  // "Read" means reached the end of the agreement, not ticked a box next to it.
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setReadAll(true);
  }, []);

  const submit = async () => {
    setError('');
    if (!name.trim() || !title.trim()) {
      setError('Please enter your name and title.');
      return;
    }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError('Please sign in the box above.');
      return;
    }
    setSaving(true);
    try {
      await api.baaAccept(orgId, {
        signer_name: name.trim(),
        signer_title: title.trim(),
        signature_data: sigRef.current.toDataURL('image/png'),
      });
      setDone(true);
      setTimeout(() => setState({ loading: false, blocked: false, pending: false, open: false }), 1800);
    } catch (err) {
      setError(err.message || 'Could not record your signature. Please try again.');
      setSaving(false);
    }
  };

  if (state.loading) return children;
  if (!state.blocked && !state.open) {
    if (!state.pending) return children;
    const eff = state.effective ? new Date(state.effective + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
    return (
      <>
        {children}
        {/* Pinned card, bottom-left (the demo guide lives bottom-right). */}
        <div className="fixed left-4 bottom-4 z-[95] max-w-[420px] bg-elevated border border-accent-light/40 rounded-[14px] shadow-lg p-4 text-[12.5px] text-primary-c">
          <div className="flex items-start gap-3">
            <div className="w-[32px] h-[32px] rounded-[9px] bg-accent-glow text-accent-light flex items-center justify-center flex-shrink-0"><ShieldCheck size={16} strokeWidth={2.2} /></div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold mb-0.5">Updated Business Associate Agreement</div>
              <div className="text-muted-c">Version {state.version}{eff ? ` takes effect ${eff}` : ''}. Please review and sign it before then. Nothing changes until you do.</div>
              <button onClick={() => setState((st) => ({ ...st, open: true }))} className="mt-2.5 px-3 py-1.5 rounded-[8px] text-[12px] font-semibold text-white gradient-accent">Review &amp; sign</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13,167,202,0.15)]';

  return (
    <div className="fixed inset-0 z-[100] bg-primary/95 backdrop-blur-sm overflow-y-auto p-4 md:p-8">
      <div className="max-w-[760px] mx-auto">
        {done ? (
          <div className="bg-elevated border border-theme rounded-xl p-10 text-center shadow-lg">
            <div className="w-[48px] h-[48px] bg-success-soft text-success rounded-[14px] flex items-center justify-center mx-auto mb-4">
              <Check size={24} strokeWidth={2.5} />
            </div>
            <h1 className="text-[22px] font-bold text-primary-c mb-2">Agreement executed</h1>
            <p className="text-[13px] text-muted-c">
              A countersigned copy is saved to your organization&apos;s records.
            </p>
          </div>
        ) : (
          <div className="bg-elevated border border-theme rounded-xl shadow-lg overflow-hidden">
            <div className="px-6 md:px-8 pt-7 pb-5 border-b border-theme">
              <div className="flex items-start gap-3">
                <div className="w-[38px] h-[38px] rounded-[10px] bg-accent-glow text-accent-light flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={19} strokeWidth={2.2} />
                </div>
                <div>
                  <h1 className="text-[19px] font-bold text-primary-c leading-tight">
                    Business Associate Agreement
                  </h1>
                  <p className="text-[12.5px] text-muted-c mt-1 leading-relaxed">
                    HIPAA requires a written agreement between {currentOrg?.name || 'your organization'} and
                    MediLink covering how we handle protected health information. Please review and
                    sign to continue.
                  </p>
                </div>
              </div>
            </div>

            <div
              ref={scrollRef}
              onScroll={onScroll}
              className="px-6 md:px-8 py-5 max-h-[42vh] overflow-y-auto border-b border-theme"
            >
              {doc ? (
                <div
                  className="legal-doc text-[12.5px] text-secondary-c"
                  dangerouslySetInnerHTML={{ __html: renderLegalMarkdown(doc.markdown) }}
                />
              ) : (
                <div className="flex items-center gap-2 text-[13px] text-muted-c py-6">
                  <LoaderCircle size={15} className="animate-spin" /> Loading the agreement…
                </div>
              )}
            </div>

            <div className="px-6 md:px-8 py-6">
              {!readAll && doc && (
                <div className="flex items-center gap-2 text-[12px] font-semibold text-amber-500 bg-amber-500/10 border border-amber-500/25 rounded-[10px] px-3.5 py-2.5 mb-4">
                  <ArrowDown size={14} className="shrink-0 animate-bounce" />
                  Scroll to the end of the agreement above — the signature fields unlock once you've read it.
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">
                    Your full name
                  </label>
                  <input className={inputClass} value={name} disabled={!readAll}
                    onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">
                    Your title
                  </label>
                  <input className={inputClass} value={title} disabled={!readAll}
                    onChange={(e) => setTitle(e.target.value)} placeholder="Practice Administrator" />
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-[12px] font-semibold text-secondary-c">Signature</label>
                  <button
                    type="button"
                    onClick={() => sigRef.current?.clear()}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-muted-c hover:text-primary-c transition-colors"
                  >
                    <RotateCcw size={11} /> Clear
                  </button>
                </div>
                <div className={`bg-tertiary border border-strong rounded-[10px] overflow-hidden ${readAll ? '' : 'opacity-50 pointer-events-none'}`}>
                  <SignatureCanvas
                    ref={sigRef}
                    penColor="#0DA7CA"
                    canvasProps={{ className: 'w-full', height: 130 }}
                  />
                </div>
              </div>

              {error && (
                <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium mb-4">
                  {error}
                </div>
              )}

              <p className="text-[11.5px] text-muted-c leading-relaxed mb-4">
                By signing you confirm you are authorised to enter into this agreement on behalf of{' '}
                <strong className="text-primary-c">{currentOrg?.name}</strong>. We record the date,
                your IP address and a cryptographic hash of the agreement text, and store a
                countersigned PDF in your organization&apos;s records.
              </p>

              <button
                onClick={submit}
                disabled={saving || !readAll}
                className="w-full py-[11px] rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {saving ? 'Recording signature…' : 'Sign and continue'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
